import { useEffect, useMemo, useRef, useState } from 'react';
import { EstimatedTag } from './ui/EstimatedTag';
import cytoscape from 'cytoscape';
import fcose from 'cytoscape-fcose';
import type { KnowledgeGraph } from '@lizrd/contracts';
import {
  buildArchitecture,
  metricsOf,
  type AccountGroup,
  type CodeBase,
  type ProviderGroup,
  type TypeGroup,
} from '../lib/architecture-model';
import {
  categoryColor,
  categoryOf,
  consoleUrl,
  LAYER_COLOR,
  PROVIDER_ACCENT,
  typeLabel,
  usd,
} from '../lib/graph-visuals';
import { useUiStore } from '../store/ui';
import { Modal } from './ui/modal';
import { NodeTypeIcon } from './node-icons';
import { nodeIconImg } from './node-icon-img';
import { Progress } from './ui/progress';
import { Pagination, usePagination } from './ui/pagination';
import { MarkdownLite } from './ui/markdown-lite';
import { ResourceDetailContent } from './ResourceDetailModal';

type GraphNode = KnowledgeGraph['nodes'][number];

// Register the compound-aware force layout once (guard against HMR / other
// modules that also register it).
try {
  cytoscape.use(fcose);
} catch {
  /* already registered */
}

const CODE_COLOR = LAYER_COLOR.code;
const accentOf = (provider: string) => PROVIDER_ACCENT[provider] ?? '#8b93a7';

/** Node diameter scaled by monthly cost (√ so a 100× spend isn't 100× wide). */
const sizeOf = (cost: number, base: number, max: number): number =>
  cost > 0 ? Math.min(max, base + Math.sqrt(cost) * 1.7) : base;

/** Mix a hex colour toward white — the light stop of each node's gloss gradient. */
function lighten(hex: string, amt = 0.55): string {
  const m = hex.replace('#', '');
  const to = (i: number) => parseInt(m.slice(i, i + 2), 16);
  const mix = (c: number) => Math.round(c + (255 - c) * amt);
  const hx = (c: number) => mix(c).toString(16).padStart(2, '0');
  return `#${hx(to(0))}${hx(to(2))}${hx(to(4))}`;
}
const gradOf = (base: string) => `${lighten(base)} ${base}`;

// --- Theme-aware palette (the canvas can't read CSS tokens) --------------------

interface Palette {
  from: string;
  to: string;
  label: string;
  halo: string;
  haloOpacity: number;
  border: string;
  spot: string;
  chip: string;
  boxOpacity: number;
  faded: number;
}
const DARK: Palette = {
  from: '#12151d',
  to: '#0a0c11',
  label: '#f2f6fb',
  halo: '#0a0c11',
  haloOpacity: 0.95,
  border: 'rgba(255,255,255,0.34)',
  spot: 'rgba(255,255,255,0.9)',
  chip: '#0e1118',
  boxOpacity: 0.06,
  faded: 0.08,
};
const LIGHT: Palette = {
  from: '#fbfcfe',
  to: '#eceff5',
  label: '#141922',
  halo: '#ffffff',
  haloOpacity: 0.95,
  border: 'rgba(0,0,0,0.22)',
  spot: 'rgba(0,0,0,0.5)',
  chip: '#ffffff',
  boxOpacity: 0.09,
  faded: 0.14,
};
const paletteFor = (theme: string): Palette => (theme === 'light' ? LIGHT : DARK);

/** Shared leaf-node style (glossy gradient + emoji inside + label below). */
const leafStyle = (pal: Palette) => ({
  'background-fill': 'radial-gradient',
  'background-gradient-stop-colors': 'data(grad)',
  'background-gradient-stop-positions': '0 100',
  'background-image': 'data(img)',
  'background-fit': 'none',
  'background-width': '78%',
  'background-height': '78%',
  'background-position-x': '50%',
  'background-position-y': '50%',
  'background-clip': 'node',
  'background-image-opacity': 1,
  'border-width': 1.5,
  'border-color': pal.border,
  color: pal.label,
  'font-size': 13,
  'font-weight': 'bold',
  'text-outline-width': 2.6,
  'text-outline-color': pal.halo,
  'text-outline-opacity': pal.haloOpacity,
  'text-valign': 'bottom',
  'text-margin-y': 8,
  'text-wrap': 'wrap',
  'text-max-width': '150px',
  label: 'data(label)',
  // Soft same-colour glow for depth.
  'underlay-color': 'data(glow)',
  'underlay-opacity': 0.16,
  'underlay-padding': 6,
  'underlay-shape': 'ellipse',
});

function buildStyle(pal: Palette): cytoscape.StylesheetCSS[] {
  const leaf = leafStyle(pal);
  // A header "chip" for the container boxes.
  const boxLabel = {
    label: 'data(label)',
    'text-valign': 'top',
    'text-halign': 'center',
    'text-margin-y': 2,
    'font-size': 13,
    'font-weight': 'bold',
    'text-background-color': pal.chip,
    'text-background-opacity': 0.92,
    'text-background-padding': 5,
    'text-background-shape': 'round-rectangle',
  };
  return [
    {
      selector: 'node[kind = "provider"]',
      style: {
        shape: 'round-rectangle',
        'background-color': 'data(accent)',
        'background-opacity': pal.boxOpacity,
        'border-width': 1.5,
        'border-color': 'data(accent)',
        'border-opacity': 0.5,
        padding: '30px',
        color: 'data(accent)',
        ...boxLabel,
      },
    },
    {
      selector: 'node[kind = "lane"]',
      style: {
        shape: 'round-rectangle',
        'background-color': CODE_COLOR,
        'background-opacity': pal.boxOpacity,
        'border-width': 1.5,
        'border-color': CODE_COLOR,
        'border-opacity': 0.45,
        'border-style': 'dashed',
        padding: '30px',
        color: CODE_COLOR,
        ...boxLabel,
      },
    },
    {
      selector: 'node[kind = "account"]',
      style: {
        ...leaf,
        shape: 'ellipse',
        width: 'data(size)',
        height: 'data(size)',
        'underlay-opacity': 0.24,
        'underlay-padding': 10,
      },
    },
    {
      selector: 'node[kind = "type"]',
      style: {
        ...leaf,
        shape: 'ellipse',
        width: 'data(size)',
        height: 'data(size)',
        'min-zoomed-font-size': 6,
      },
    },
    {
      selector: 'node[kind = "code"]',
      style: { ...leaf, shape: 'round-rectangle', width: 66, height: 52 },
    },
    {
      // $0 resources recede so real spend pops.
      selector: 'node[muted = 1]',
      style: {
        'background-image-opacity': 0.7,
        'underlay-opacity': 0.04,
        opacity: 0.88,
        'border-color': pal.border,
      },
    },
    {
      // Account → resource type: a subtle, slow marching flow — visible at rest.
      selector: 'edge[kind = "spoke"]',
      style: {
        width: 1.4,
        'line-color': 'data(accent)',
        opacity: 0.5,
        'line-style': 'dashed',
        'line-dash-pattern': [4, 4],
        'curve-style': 'bezier',
        'target-arrow-shape': 'none',
      },
    },
    {
      // Code → account: gradient (code → cloud) with a marching-dash flow.
      selector: 'edge[kind = "owns"]',
      style: {
        width: 1.8,
        'line-fill': 'linear-gradient',
        'line-gradient-stop-colors': 'data(grad)',
        'line-gradient-stop-positions': '0 100',
        'line-style': 'dashed',
        'line-dash-pattern': [7, 6],
        opacity: 0.8,
        'curve-style': 'unbundled-bezier',
        'target-arrow-shape': 'none',
      },
    },
    { selector: '.faded', style: { opacity: pal.faded } },
    {
      selector: 'node.spot',
      style: {
        'underlay-opacity': 0.5,
        'underlay-padding': 14,
        'border-color': pal.spot,
        'border-width': 2,
      },
    },
    { selector: 'edge.hot', style: { opacity: 0.95, width: 2.6 } },
    { selector: 'node:selected', style: { 'border-width': 3, 'border-color': pal.spot } },
  ] as unknown as cytoscape.StylesheetCSS[];
}

function buildElements(model: ReturnType<typeof buildArchitecture>): cytoscape.ElementDefinition[] {
  const els: cytoscape.ElementDefinition[] = [];
  const accentByAccount = new Map<string, string>();

  for (const prov of model.providers) {
    const accent = accentOf(prov.provider);
    const nAcc = prov.accounts.length;
    els.push({
      data: {
        id: `prov:${prov.provider}`,
        kind: 'provider',
        accent,
        label: `${prov.provider} · ${nAcc} account${nAcc !== 1 ? 's' : ''} · ${usd(prov.totalCost)}/mo`,
      },
    });
    for (const acc of prov.accounts) {
      accentByAccount.set(acc.key, accent);
      els.push({
        data: {
          id: `acct:${acc.key}`,
          parent: `prov:${prov.provider}`,
          kind: 'account',
          grad: gradOf(accent),
          glow: accent,
          img: nodeIconImg('cloud.account', '#ffffff'),
          size: sizeOf(acc.totalCost, 72, 124),
          label: acc.name,
        },
      });
      for (const t of acc.types) {
        const muted = t.totalCost === 0 ? 1 : 0;
        const base = muted ? '#8b93a7' : categoryColor(categoryOf(t.type));
        els.push({
          data: {
            id: `type:${t.key}`,
            parent: `prov:${prov.provider}`,
            kind: 'type',
            grad: gradOf(base),
            glow: base,
            img: nodeIconImg(t.type, '#ffffff'),
            size: muted ? 44 : sizeOf(t.totalCost, 54, 110),
            label: typeLabel(t.type, t.provider),
            muted,
          },
        });
        els.push({
          data: {
            id: `spoke:${t.key}`,
            source: `type:${t.key}`,
            target: `acct:${acc.key}`,
            kind: 'spoke',
            accent,
          },
        });
      }
    }
  }

  if (model.codeBases.length) {
    els.push({ data: { id: 'lane:code', kind: 'lane', label: 'Code' } });
    for (const cb of model.codeBases) {
      els.push({
        data: {
          id: `code:${cb.id}`,
          parent: 'lane:code',
          kind: 'code',
          grad: gradOf(CODE_COLOR),
          glow: CODE_COLOR,
          img: nodeIconImg('repository', '#ffffff'),
          label: cb.name,
        },
      });
      for (const key of cb.ownedAccountKeys) {
        const accent = accentByAccount.get(key) ?? '#8b93a7';
        els.push({
          data: {
            id: `owns:${cb.id}:${key}`,
            source: `code:${cb.id}`,
            target: `acct:${key}`,
            kind: 'owns',
            grad: `${CODE_COLOR} ${accent}`,
          },
        });
      }
    }
  }

  return els;
}

/** Left→right "software delivery flow" positions: Code | Accounts | Resource types. */
function flowPositions(
  model: ReturnType<typeof buildArchitecture>,
): Map<string, cytoscape.Position> {
  const pos = new Map<string, cytoscape.Position>();
  const X_ACCT = 340;
  const X_TYPE = 700;
  const ROW = 82;
  let y = 60;
  for (const prov of model.providers) {
    for (const acc of prov.accounts) {
      const n = Math.max(1, acc.types.length);
      const top = y;
      acc.types.forEach((t, i) => pos.set(`type:${t.key}`, { x: X_TYPE, y: top + i * ROW }));
      pos.set(`acct:${acc.key}`, { x: X_ACCT, y: top + ((n - 1) * ROW) / 2 });
      y = top + n * ROW + 36;
    }
  }
  const codes = model.codeBases;
  const codeTop = Math.max(60, (y - codes.length * ROW) / 2);
  codes.forEach((c, i) => pos.set(`code:${c.id}`, { x: 0, y: codeTop + i * ROW }));
  return pos;
}

// --- View + hover models -------------------------------------------------------

type View =
  | { kind: 'account'; account: AccountGroup; provider: string }
  | { kind: 'type'; type: TypeGroup; account: AccountGroup }
  | { kind: 'resource'; node: GraphNode }
  | { kind: 'code'; code: CodeBase };

type Hover =
  | {
      kind: 'type';
      iconType: string;
      name: string;
      count: number;
      cost: number;
      cpu: number | null;
      mem: number | null;
    }
  | { kind: 'account'; iconType: string; name: string; count: number; cost: number; sub: string }
  | { kind: 'code'; iconType: string; name: string; count: number; cost: number; sub: string };

type Mode = 'constellation' | 'flow';

/**
 * The Explorer "Architecture" view: an interactive, theme-aware topology that
 * aggregates the Customer Knowledge Graph to `code bases + providers×accounts×
 * types`, so it stays legible at any scale. Glossy emoji-in-shape nodes gently
 * float and glow (no dragging); hover for a summary, click for the drill-in
 * modal. Toggle a force "web" or a left→right delivery "flow".
 */
export function CostArchitecture({ graph, envId }: { graph: KnowledgeGraph; envId: string }) {
  const model = useMemo(() => buildArchitecture(graph), [graph]);
  const theme = useUiStore((s) => s.theme);
  const pal = paletteFor(theme);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  const rafRef = useRef<number | null>(null);
  const bloomTimer = useRef<number | null>(null);
  const finishTimer = useRef<number | null>(null);
  const pausedRef = useRef(false);
  const baseRef = useRef<Map<string, { x: number; y: number; phase: number }>>(new Map());
  const hoverNodeRef = useRef<cytoscape.NodeSingular | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  // The living drift animation only runs while the graph is actually on-screen and
  // the user hasn't asked for reduced motion — otherwise it repaints the canvas
  // every frame forever, a sustained GPU/CPU load that degrades long sessions.
  const visibleRef = useRef(true);
  const reducedMotionRef = useRef(false);

  const [mode, setMode] = useState<Mode>('constellation');
  const [modalView, setModalView] = useState<View | null>(null);
  const [hover, setHover] = useState<Hover | null>(null);
  const [ready, setReady] = useState(false);

  const typeById = useMemo(() => {
    const m = new Map<string, { type: TypeGroup; account: AccountGroup }>();
    for (const p of model.providers)
      for (const a of p.accounts)
        for (const t of a.types) m.set(`type:${t.key}`, { type: t, account: a });
    return m;
  }, [model]);
  const accountById = useMemo(() => {
    const m = new Map<string, { account: AccountGroup; provider: ProviderGroup }>();
    for (const p of model.providers)
      for (const a of p.accounts) m.set(`acct:${a.key}`, { account: a, provider: p });
    return m;
  }, [model]);
  const codeById = useMemo(() => {
    const m = new Map<string, CodeBase>();
    for (const c of model.codeBases) m.set(`code:${c.id}`, c);
    return m;
  }, [model]);

  pausedRef.current = modalView != null;

  // Init once.
  useEffect(() => {
    if (!containerRef.current || cyRef.current) return;
    const cy = cytoscape({
      container: containerRef.current,
      style: buildStyle(paletteFor(useUiStore.getState().theme)),
      wheelSensitivity: 0.2,
      autoungrabify: true, // clicking only — nodes can't be dragged
      minZoom: 0.2,
      maxZoom: 2.5,
    });
    cyRef.current = cy;
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (bloomTimer.current) window.clearTimeout(bloomTimer.current);
      if (finishTimer.current) window.clearTimeout(finishTimer.current);
      cy.destroy();
      cyRef.current = null;
    };
  }, []);

  // Pause the perpetual drift animation whenever the graph is scrolled off-screen
  // (and honor prefers-reduced-motion). The browser already halts requestAnimationFrame
  // on hidden tabs; this covers the "graph below the fold" case, so the canvas is
  // never repainted when you can't see it — the main lever against long-session
  // slow-downs/crashes.
  useEffect(() => {
    reducedMotionRef.current =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        const onScreen = entries[0]?.isIntersecting ?? true;
        const was = visibleRef.current;
        visibleRef.current = onScreen;
        // The float loop halts itself and nulls rafRef when off-screen; restart it
        // only when it scrolls back into view and isn't already running.
        if (
          onScreen &&
          !was &&
          rafRef.current == null &&
          cyRef.current &&
          !reducedMotionRef.current
        ) {
          startFloat();
        }
      },
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Re-skin on theme flip (positions preserved).
  useEffect(() => {
    cyRef.current?.style(buildStyle(pal));
  }, [pal]);

  // Bind hover + tap handlers to the latest lookup maps.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const focusable = 'node[kind = "type"], node[kind = "account"], node[kind = "code"]';

    const buildHover = (id: string): Hover | null => {
      if (id.startsWith('type:')) {
        const h = typeById.get(id);
        if (!h) return null;
        const t = h.type;
        return {
          kind: 'type',
          iconType: t.type,
          name: typeLabel(t.type, t.provider),
          count: t.count,
          cost: t.totalCost,
          cpu: t.avgCpuPct,
          mem: t.avgMemPct,
        };
      }
      if (id.startsWith('acct:')) {
        const h = accountById.get(id);
        if (!h) return null;
        const a = h.account;
        return {
          kind: 'account',
          iconType: 'cloud.account',
          name: a.name,
          count: a.resourceCount,
          cost: a.totalCost,
          sub: `${h.provider.provider}${a.accountId ? ` · ${a.accountId}` : ''}`,
        };
      }
      if (id.startsWith('code:')) {
        const c = codeById.get(id);
        if (!c) return null;
        const n = c.ownedAccountKeys.length;
        return {
          kind: 'code',
          iconType: 'repository',
          name: c.name,
          count: c.runsCount,
          cost: c.runsCost,
          sub: `across ${n} account${n !== 1 ? 's' : ''}`,
        };
      }
      return null;
    };

    cy.removeListener('mouseover');
    cy.removeListener('mouseout');
    cy.removeListener('tap', 'node[kind = "type"]');
    cy.removeListener('tap', 'node[kind = "account"]');
    cy.removeListener('tap', 'node[kind = "code"]');

    cy.on('mouseover', focusable, (e) => {
      const n = e.target;
      cy.elements().not(n.closedNeighborhood().union(n.ancestors())).addClass('faded');
      n.connectedEdges().addClass('hot');
      n.addClass('spot');
      hoverNodeRef.current = n;
      setHover(buildHover(n.id()));
      const tip = tooltipRef.current;
      if (tip) {
        const rp = n.renderedPosition();
        tip.style.transform = `translate(${rp.x}px, ${rp.y - n.renderedHeight() / 2 - 10}px)`;
      }
    });
    cy.on('mouseout', focusable, () => {
      cy.elements().removeClass('faded hot spot');
      hoverNodeRef.current = null;
      setHover(null);
    });
    cy.on('tap', 'node[kind = "type"]', (e) => {
      const h = typeById.get(e.target.id());
      if (h) setModalView({ kind: 'type', type: h.type, account: h.account });
    });
    cy.on('tap', 'node[kind = "account"]', (e) => {
      const h = accountById.get(e.target.id());
      if (h) setModalView({ kind: 'account', account: h.account, provider: h.provider.provider });
    });
    cy.on('tap', 'node[kind = "code"]', (e) => {
      const h = codeById.get(e.target.id());
      if (h) setModalView({ kind: 'code', code: h });
    });
  }, [typeById, accountById, codeById]);

  // (Re)build + layout when the model or mode changes, then bloom in + float.
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (bloomTimer.current) window.clearTimeout(bloomTimer.current);
    if (finishTimer.current) window.clearTimeout(finishTimer.current);
    setReady(false);
    cy.elements().remove();
    cy.add(buildElements(model));

    let done = false;
    const finish = () => {
      if (done || !cyRef.current) return;
      done = true;
      cyRef.current.fit(undefined, 22);
      bloom();
    };

    const layout =
      mode === 'flow'
        ? cy.layout({
            name: 'preset',
            positions: ((n: cytoscape.NodeSingular) => flowPositions(model).get(n.id())) as never,
            fit: true,
            padding: 22,
            animate: false,
          } as cytoscape.LayoutOptions)
        : cy.layout({
            name: 'fcose',
            quality: 'default',
            animate: false,
            randomize: true,
            packComponents: true,
            nodeRepulsion: 10000,
            idealEdgeLength: (edge: cytoscape.EdgeSingular) =>
              edge.data('kind') === 'owns' ? 150 : 80,
            nodeSeparation: 110,
            gravity: 0.4,
            gravityCompound: 1.6,
            padding: 30,
          } as cytoscape.LayoutOptions);

    layout.one('layoutstop', finish);
    layout.run();
    finishTimer.current = window.setTimeout(finish, 350);
  }, [model, mode]);

  /** Materialise the graph: fade the canvas in while nodes ease out from a
   * slightly contracted start — organic, not a mechanical spring. */
  function bloom() {
    const cy = cyRef.current;
    if (!cy) return;
    const leaves = cy.nodes('[kind = "type"], [kind = "account"], [kind = "code"]');
    const bb = leaves.boundingBox();
    const cx = (bb.x1 + bb.x2) / 2;
    const cyc = (bb.y1 + bb.y2) / 2;
    const base = new Map<string, { x: number; y: number; phase: number }>();
    leaves.forEach((n, i) => {
      const p = n.position();
      base.set(n.id(), { x: p.x, y: p.y, phase: i * 0.7 });
    });
    baseRef.current = base;

    cy.batch(() =>
      leaves.forEach((n) => {
        const b = base.get(n.id())!;
        n.position({ x: cx + (b.x - cx) * 0.82, y: cyc + (b.y - cyc) * 0.82 });
      }),
    );
    setReady(true);

    let maxDelay = 0;
    leaves.forEach((n, i) => {
      const b = base.get(n.id())!;
      const delay = Math.min(320, i * 8);
      maxDelay = Math.max(maxDelay, delay);
      n.delay(delay).animate(
        { position: { x: b.x, y: b.y } },
        { duration: 620, easing: 'ease-out' },
      );
    });
    bloomTimer.current = window.setTimeout(() => startFloat(), maxDelay + 700);
  }

  /** The living loop: gentle node drift, a soft pulse on account hubs, a marching
   * flow along ownership edges, and tooltip tracking. Bounded node count keeps it
   * cheap; nothing is ever draggable. */
  function startFloat() {
    // Skip the perpetual drift entirely for reduced-motion users — the static
    // layout is the whole point; the animation is pure polish.
    if (reducedMotionRef.current || !cyRef.current) return;
    // Never stack loops (this is called on init/rebuild and on scroll-back-into-view).
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const base = baseRef.current;
    let dashOwns = 0; // code → cloud: slow
    let dashSpoke = 0; // account → resource: slower + subtler
    let lastPaint = 0;
    // Throttle the canvas repaint to ~20 fps — the drift is subtle (±3px), so a
    // third of the frames looks identical while cutting GPU/CPU work ~3×.
    const FRAME_MS = 50;
    const tick = () => {
      const cy = cyRef.current;
      if (!cy) return;
      // Halt (stop rescheduling) when scrolled off-screen — the IntersectionObserver
      // restarts the loop when the graph comes back into view.
      if (!visibleRef.current) {
        rafRef.current = null;
        return;
      }
      const now = performance.now();
      const t = now / 1000;
      if (!pausedRef.current && now - lastPaint >= FRAME_MS) {
        lastPaint = now;
        cy.batch(() => {
          cy.nodes('[kind = "type"], [kind = "account"], [kind = "code"]').forEach((n) => {
            const b = base.get(n.id());
            if (!b) return;
            n.position({
              x: b.x + Math.sin(t * 0.6 + b.phase) * 3.2,
              y: b.y + Math.cos(t * 0.5 + b.phase) * 3.2,
            });
          });
          cy.nodes('[kind = "account"]').forEach((n) => {
            if (!n.hasClass('spot'))
              n.style('underlay-opacity', 0.2 + Math.sin(t * 1.3 + 0.5) * 0.09);
          });
          dashOwns -= 0.3;
          dashSpoke -= 0.12;
          cy.edges('[kind = "owns"]').style('line-dash-offset', dashOwns);
          cy.edges('[kind = "spoke"]').style('line-dash-offset', dashSpoke);
        });
      }
      const hn = hoverNodeRef.current;
      const tip = tooltipRef.current;
      if (hn && tip) {
        const rp = hn.renderedPosition();
        tip.style.transform = `translate(${rp.x}px, ${rp.y - hn.renderedHeight() / 2 - 10}px)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }

  const empty = model.providers.length === 0 && model.codeBases.length === 0;

  return (
    <div className="space-y-2">
      <div
        className="relative overflow-hidden rounded-md border border-hairline"
        style={{ height: 560 }}
      >
        <div
          ref={containerRef}
          className={`h-full w-full transition-opacity duration-700 ease-out ${ready ? 'opacity-100' : 'opacity-0'}`}
          style={{ background: `radial-gradient(circle at 50% 38%, ${pal.from}, ${pal.to})` }}
        />
        {empty && (
          <div className="absolute inset-0 grid place-items-center px-6 text-center text-sm text-muted">
            No architecture yet — connect a cloud integration and run a sync to map this domain.
          </div>
        )}
        {!empty && (
          <>
            <ArchitectureLegend providers={model.providers} hasCode={model.codeBases.length > 0} />
            <div className="absolute right-2 top-2 inline-flex overflow-hidden rounded-sm border border-border bg-surface/80 text-[0.65rem] backdrop-blur">
              {(['constellation', 'flow'] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`px-2.5 py-1 transition-colors ${mode === m ? 'bg-iris text-on-iris' : 'text-muted hover:text-bone'}`}
                >
                  {m === 'constellation' ? '✦ Web' : '⇢ Flow'}
                </button>
              ))}
            </div>
            <button
              onClick={() =>
                cyRef.current?.animate({
                  fit: { eles: cyRef.current.elements(), padding: 22 },
                  duration: 250,
                })
              }
              className="absolute bottom-2 right-2 rounded-sm border border-border bg-surface/80 px-2 py-1 text-[0.65rem] text-muted backdrop-blur transition-colors hover:text-bone"
            >
              ⤢ Fit
            </button>
          </>
        )}
        {/* Hover tooltip — always mounted so it can be positioned imperatively. */}
        <div
          ref={tooltipRef}
          className="pointer-events-none absolute left-0 top-0 z-20"
          style={{ display: hover ? 'block' : 'none' }}
        >
          <div className="-translate-x-1/2 -translate-y-full">
            {hover && (
              <div className="min-w-[9rem] max-w-[15rem] rounded-md border border-hairline bg-surface/95 px-3 py-2 shadow-xl backdrop-blur">
                <div className="flex items-center gap-1.5">
                  <NodeTypeIcon type={hover.iconType} className="h-4 w-4 text-muted" />
                  <span className="truncate text-xs font-bold text-bone">{hover.name}</span>
                </div>
                <div className="mt-0.5 text-[0.7rem] text-muted">
                  {hover.kind === 'type'
                    ? `×${hover.count}`
                    : `${hover.count} resource${hover.count !== 1 ? 's' : ''}`}{' '}
                  · <span className="font-semibold text-iris">{usd(hover.cost)}/mo</span>
                </div>
                {hover.kind !== 'type' && hover.sub && (
                  <div className="text-[0.65rem] text-muted">{hover.sub}</div>
                )}
                {hover.kind === 'type' && (hover.cpu != null || hover.mem != null) && (
                  <div className="mt-1.5 space-y-1">
                    {hover.cpu != null && <MetricBar label="Avg CPU" pct={hover.cpu} />}
                    {hover.mem != null && <MetricBar label="Avg Mem" pct={hover.mem} />}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <p className="text-[0.7rem] text-muted">
        Aggregated by resource type — hover for a summary, click any node for details. Dashed lines
        trace which clouds each code base runs in.
      </p>

      {modalView && (
        <ArchitectureModal initial={modalView} envId={envId} onClose={() => setModalView(null)} />
      )}
    </div>
  );
}

// --- Legend --------------------------------------------------------------------

function ArchitectureLegend({
  providers,
  hasCode,
}: {
  providers: ProviderGroup[];
  hasCode: boolean;
}) {
  return (
    <div className="pointer-events-none absolute left-2 top-2 flex flex-col gap-1 rounded-sm bg-surface/80 p-2 text-[0.62rem] backdrop-blur">
      {hasCode && (
        <span className="flex items-center gap-1.5 text-muted">
          <span
            className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: CODE_COLOR }}
          />
          Code base
        </span>
      )}
      {providers.map((p) => (
        <span key={p.provider} className="flex items-center gap-1.5 text-muted">
          <span
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: accentOf(p.provider) }}
          />
          {p.provider}
        </span>
      ))}
    </div>
  );
}

// --- Metric bits (cost + operational metrics are the headline) -----------------

/** A compact labelled utilisation bar. */
function MetricBar({ label, pct }: { label: string; pct: number }) {
  return (
    <div className="min-w-0 flex-1">
      <div className="flex justify-between text-[0.62rem] text-muted">
        <span className="truncate">{label}</span>
        <span className="text-haze">{Math.round(pct)}%</span>
      </div>
      <Progress value={pct} variant={pct >= 80 ? 'warning' : 'iris'} size="sm" />
    </div>
  );
}

/** CPU/memory bars for one resource, or a muted "no metrics" note. */
function ResourceMetrics({ node }: { node: GraphNode }) {
  const { cpuPct, memPct } = metricsOf(node);
  if (cpuPct == null && memPct == null)
    return <span className="text-[0.62rem] text-muted">No utilisation metrics</span>;
  return (
    <div className="flex items-center gap-3">
      {cpuPct != null && <MetricBar label="CPU" pct={cpuPct} />}
      {memPct != null && <MetricBar label="Mem" pct={memPct} />}
    </div>
  );
}

function costParts(node: GraphNode): { text: string; usageBased: boolean } {
  const cost = node.properties.monthlyCost;
  const usageBased = node.properties.costSource === 'usage-based';
  if (typeof cost === 'number') return { text: usd(cost), usageBased };
  return { text: usageBased ? 'Usage-based' : '—', usageBased };
}

// --- The single navigable modal ------------------------------------------------

function crumb(v: View): string {
  switch (v.kind) {
    case 'account':
      return v.account.name;
    case 'type':
      return typeLabel(v.type.type, v.type.provider);
    case 'resource':
      return v.node.name;
    case 'code':
      return v.code.name;
  }
}

function ArchitectureModal({
  initial,
  envId,
  onClose,
}: {
  initial: View;
  envId: string;
  onClose: () => void;
}) {
  const [stack, setStack] = useState<View[]>([initial]);
  useEffect(() => setStack([initial]), [initial]);
  const view = stack[stack.length - 1]!;
  const push = (v: View) => setStack((s) => [...s, v]);
  const back = () => setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));

  const titleIconType =
    view.kind === 'account'
      ? 'cloud.account'
      : view.kind === 'code'
        ? 'repository'
        : view.kind === 'type'
          ? view.type.type
          : view.node.type;
  const titleText =
    view.kind === 'account'
      ? view.account.name
      : view.kind === 'code'
        ? view.code.name
        : view.kind === 'type'
          ? `${typeLabel(view.type.type, view.type.provider)} ×${view.type.count}`
          : view.node.name;

  return (
    <Modal
      open
      onClose={onClose}
      maxWidth="3xl"
      title={
        <div className="min-w-0">
          {stack.length > 1 && (
            <button
              onClick={back}
              className="mb-1 inline-flex items-center gap-1 rounded-sm text-[0.7rem] text-muted transition-colors hover:text-bone"
            >
              ← Back
            </button>
          )}
          <div className="flex items-center gap-2">
            <span className="text-xl leading-none" aria-hidden>
              <NodeTypeIcon type={titleIconType} className="h-5 w-5 text-muted" />
            </span>
            <div className="min-w-0">
              <h3 className="truncate font-display text-base font-bold text-bone">{titleText}</h3>
              {stack.length > 1 && (
                <p className="truncate text-[0.7rem] text-muted">
                  {stack.map(crumb).join('  ▸  ')}
                </p>
              )}
            </div>
          </div>
        </div>
      }
    >
      <div key={`${stack.length}:${view.kind}`}>
        {view.kind === 'account' && (
          <AccountView
            account={view.account}
            provider={view.provider}
            onPickType={(t) => push({ kind: 'type', type: t, account: view.account })}
          />
        )}
        {view.kind === 'type' && (
          <TypeView
            type={view.type}
            account={view.account}
            onPickResource={(n) => push({ kind: 'resource', node: n })}
          />
        )}
        {view.kind === 'resource' && <ResourceView node={view.node} envId={envId} />}
        {view.kind === 'code' && <CodeView code={view.code} />}
      </div>
    </Modal>
  );
}

function AccountView({
  account,
  provider,
  onPickType,
}: {
  account: AccountGroup;
  provider: string;
  onPickType: (t: TypeGroup) => void;
}) {
  const pg = usePagination(account.types);
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">
            Monthly Cost <EstimatedTag />
          </div>
          <div className="font-display text-2xl font-bold text-iris">
            {usd(account.totalCost)}
            <span className="text-sm text-muted">/mo</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-bone">
            {account.resourceCount} resource{account.resourceCount !== 1 ? 's' : ''}
          </div>
          <div className="text-[0.7rem] text-muted">
            {provider}
            {account.accountId ? ` · ${account.accountId}` : ''}
          </div>
        </div>
      </div>
      <div>
        <ul className="divide-y divide-hairline">
          {pg.pageItems.map((t) => (
            <li key={t.key}>
              <button
                onClick={() => onPickType(t)}
                className="flex w-full items-center gap-2 py-2 text-left transition-colors hover:bg-surface-2/40"
              >
                <NodeTypeIcon type={t.type} className="h-4 w-4 text-muted" />
                <span className="text-sm text-bone">{typeLabel(t.type, t.provider)}</span>
                <span className="text-[0.65rem] text-muted">×{t.count}</span>
                <span className="ml-auto font-display text-sm font-bold text-bone">
                  {usd(t.totalCost)}
                  <span className="text-[0.62rem] text-muted">/mo</span>
                </span>
                <span className="text-muted">›</span>
              </button>
            </li>
          ))}
        </ul>
        <Pagination
          page={pg.page}
          pageCount={pg.pageCount}
          total={pg.total}
          start={pg.start}
          shown={pg.shown}
          onPrev={pg.prev}
          onNext={pg.next}
        />
      </div>
    </div>
  );
}

function TypeView({
  type,
  account,
  onPickResource,
}: {
  type: TypeGroup;
  account: AccountGroup;
  onPickResource: (n: GraphNode) => void;
}) {
  const pg = usePagination(type.resources);
  return (
    <div className="space-y-4">
      {/* Headline: cost + aggregate operational metrics. */}
      <div className="flex flex-wrap items-end gap-x-8 gap-y-3 rounded-md border border-hairline bg-surface-2/40 p-3">
        <div>
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Monthly Cost</div>
          <div className="font-display text-2xl font-bold text-iris">
            {usd(type.totalCost)}
            <span className="text-sm text-muted">/mo</span>
          </div>
        </div>
        {type.avgCpuPct != null && (
          <div className="min-w-[7rem]">
            <div className="text-[0.7rem] uppercase tracking-wide text-muted">Avg CPU</div>
            <MetricBar label="across resources" pct={type.avgCpuPct} />
          </div>
        )}
        {type.avgMemPct != null && (
          <div className="min-w-[7rem]">
            <div className="text-[0.7rem] uppercase tracking-wide text-muted">Avg Memory</div>
            <MetricBar label="across resources" pct={type.avgMemPct} />
          </div>
        )}
        <div className="ml-auto inline-flex items-center gap-1 text-right text-[0.7rem] text-muted">
          in <NodeTypeIcon type="cloud.account" className="h-3 w-3" /> {account.name}
        </div>
      </div>

      {/* Per-resource rows — cost bold, utilisation prominent. */}
      <div>
        <ul className="divide-y divide-hairline">
          {pg.pageItems.map((n) => {
            const { text, usageBased } = costParts(n);
            const region = typeof n.properties.region === 'string' ? n.properties.region : null;
            const url = consoleUrl(n);
            return (
              <li key={n.id}>
                <button
                  onClick={() => onPickResource(n)}
                  className="grid w-full grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 py-2.5 text-left transition-colors hover:bg-surface-2/40 sm:grid-cols-[1fr_11rem_auto]"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm text-bone" title={n.name}>
                      {n.name}
                    </div>
                    {region && <div className="text-[0.62rem] text-muted">{region}</div>}
                  </div>
                  <div className="hidden sm:block">
                    <ResourceMetrics node={n} />
                  </div>
                  <div className="text-right">
                    <span
                      className={`font-display text-sm font-bold ${usageBased ? 'text-haze' : 'text-bone'}`}
                    >
                      {text}
                    </span>
                    {!usageBased && text !== '—' && (
                      <span className="text-[0.62rem] text-muted">/mo</span>
                    )}
                    {url && <div className="text-[0.6rem] text-viridian">Details ↗</div>}
                  </div>
                  {/* Metrics fold under the row on narrow screens. */}
                  <div className="col-span-2 sm:hidden">
                    <ResourceMetrics node={n} />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
        <Pagination
          page={pg.page}
          pageCount={pg.pageCount}
          total={pg.total}
          start={pg.start}
          shown={pg.shown}
          onPrev={pg.prev}
          onNext={pg.next}
        />
      </div>
    </div>
  );
}

function ResourceView({ node, envId }: { node: GraphNode; envId: string }) {
  const url = consoleUrl(node);
  return (
    <div className="space-y-4">
      <ResourceDetailContent node={node} envId={envId} />
      <div className="border-t border-hairline pt-3 text-xs">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-viridian hover:underline"
          >
            Open in cloud console ↗
          </a>
        ) : (
          <span className="text-muted">No console link available</span>
        )}
      </div>
    </div>
  );
}

function CodeView({ code }: { code: CodeBase }) {
  const p = code.node.properties as Record<string, unknown>;
  const summary =
    typeof p.summary === 'string'
      ? p.summary
      : typeof p.readmeExcerpt === 'string'
        ? p.readmeExcerpt
        : null;
  const languages =
    p.languages && typeof p.languages === 'object'
      ? (
          Object.entries(p.languages as Record<string, unknown>).filter(
            ([, v]) => typeof v === 'number',
          ) as [string, number][]
        )
          .sort((a, b) => b[1] - a[1])
          .map(([lang]) => lang)
      : [];
  const caps = Array.isArray(p.capabilities)
    ? (p.capabilities.filter((c) => typeof c === 'string') as string[])
    : [];
  const pg = usePagination(code.runs);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Runs (monthly)</div>
          <div className="font-display text-2xl font-bold text-iris">
            {usd(code.runsCost)}
            <span className="text-sm text-muted">/mo</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-bone">
            {code.runsCount} resource{code.runsCount !== 1 ? 's' : ''}
          </div>
          <div className="text-[0.7rem] text-muted">
            across {code.ownedAccountKeys.length} account
            {code.ownedAccountKeys.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {summary && (
        <div className="border-t border-hairline pt-3">
          <MarkdownLite text={summary} />
        </div>
      )}

      {languages.length > 0 && (
        <div className="space-y-2 border-t border-hairline pt-3">
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Languages</div>
          <div className="flex flex-wrap gap-1.5">
            {languages.map((lang) => (
              <span
                key={lang}
                className="rounded-full border border-border bg-surface-2 px-2 py-0.5 text-[0.65rem] text-bone"
              >
                {lang}
              </span>
            ))}
          </div>
        </div>
      )}

      {caps.length > 0 && (
        <div className="space-y-2 border-t border-hairline pt-3">
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">Capabilities</div>
          <div className="flex flex-wrap gap-1.5">
            {caps.map((c) => (
              <span
                key={c}
                className="rounded-full border border-border bg-surface-2 px-2 py-0.5 text-[0.65rem] text-bone"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {code.runs.length > 0 && (
        <div className="space-y-1 border-t border-hairline pt-3">
          <div className="text-[0.7rem] uppercase tracking-wide text-muted">What it runs</div>
          <ul className="divide-y divide-hairline">
            {pg.pageItems.map((r) => (
              <li key={`${r.provider}|${r.type}`} className="flex items-center gap-2 py-2">
                <NodeTypeIcon type={r.type} className="h-4 w-4 text-muted" />
                <span className="text-sm text-bone">{typeLabel(r.type, r.provider)}</span>
                <span className="text-[0.65rem] text-muted">×{r.count}</span>
                <span className="ml-auto font-display text-sm font-bold text-bone">
                  {usd(r.totalCost)}
                  <span className="text-[0.62rem] text-muted">/mo</span>
                </span>
              </li>
            ))}
          </ul>
          <Pagination
            page={pg.page}
            pageCount={pg.pageCount}
            total={pg.total}
            start={pg.start}
            shown={pg.shown}
            onPrev={pg.prev}
            onNext={pg.next}
          />
        </div>
      )}
    </div>
  );
}
